<template>
    <div class="page-container">
        <div class="settings-form">
            <!-- 顶部引导区域 -->
            <div class="guide-section">
                <div class="guide-content">
                    <h2 class="guide-title">
                        <el-icon class="guide-icon">
                            <User />
                        </el-icon>
                        用户管理
                    </h2>
                    <p class="guide-desc">管理商户的用户账号信息</p>
                </div>
                <div class="guide-actions">
                    <el-button type="primary" @click="fetchUserList" :loading="loading.refresh">
                        <el-icon>
                            <Refresh />
                        </el-icon>
                        刷新列表
                    </el-button>
                </div>
            </div>

            <!-- 搜索区域 -->
            <div class="search-bar">
                <el-input v-model="searchForm.phone" placeholder="手机号" clearable size="large"
                    style="width: 180px; margin-right: 15px" />
                <el-input v-model="searchForm.nickname" placeholder="昵称" clearable size="large"
                    style="width: 180px; margin-right: 15px" />
                <el-select v-model="searchForm.status" placeholder="状态" clearable size="large"
                    style="width: 120px; margin-right: 15px">
                    <el-option label="全部" value="" />
                    <el-option label="正常" :value="1" />
                    <el-option label="禁用" :value="0" />
                </el-select>

                <el-button type="primary" size="large" @click="handleSearch">
                    <el-icon>
                        <Search />
                    </el-icon>
                    搜索
                </el-button>
            </div>

            <!-- 用户列表 -->
            <el-table :data="userList" stripe style="width: 100%; margin-top: 20px" v-loading="loading.list">
                <el-table-column prop="phone" label="手机号" width="150" />
                <el-table-column prop="nickname" label="昵称" width="150" />
                <el-table-column label="头像" width="80">
                    <template #default="{ row }">
                        <el-avatar :size="40" :src="row.avatar" v-if="row.avatar">
                            <el-icon>
                                <User />
                            </el-icon>
                        </el-avatar>
                        <el-avatar :size="40" v-else>
                            <el-icon>
                                <User />
                            </el-icon>
                        </el-avatar>
                    </template>
                </el-table-column>
                <el-table-column label="性别" width="80">
                    <template #default="{ row }">
                        <el-tag v-if="row.gender === 1" type="primary">男</el-tag>
                        <el-tag v-else-if="row.gender === 2" type="danger">女</el-tag>
                        <el-tag v-else type="info">未知</el-tag>
                    </template>
                </el-table-column>
                <el-table-column label="密码状态" width="100">
                    <template #default="{ row }">
                        <el-tag v-if="row.has_password" type="success">已设置</el-tag>
                        <el-tag v-else type="warning">未设置</el-tag>
                    </template>
                </el-table-column>
                <el-table-column label="状态" width="80">
                    <template #default="{ row }">
                        <el-tag v-if="row.status" type="success">正常</el-tag>
                        <el-tag v-else type="danger">禁用</el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="last_login_time" label="最后登录" width="180" />
                <el-table-column prop="last_login_ip" label="登录IP" width="120" />
                <el-table-column prop="create_time" label="创建时间" width="180" />
                <el-table-column prop="update_time" label="更新时间" width="180" />

                <el-table-column label="操作" width="240" fixed="right">
                    <template #default="scope">
                        <el-button type="primary" size="small" @click="openEditDialog(scope.row)">
                            <el-icon>
                                <Edit />
                            </el-icon>
                            编辑
                        </el-button>

                        <el-button type="info" size="small" @click="openResetPasswordDialog(scope.row)">
                            <el-icon>
                                <Key />
                            </el-icon>
                            重置密码
                        </el-button>
                    </template>
                </el-table-column>
            </el-table>

            <!-- 分页组件 -->
            <div class="pagination-container">
                <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
                    :total="pagination.total" :page-sizes="[10, 20, 30, 50]"
                    layout="total, sizes, prev, pager, next, jumper" @size-change="fetchUserList"
                    @current-change="fetchUserList" />
            </div>
        </div>

        <!-- 编辑用户对话框 -->
        <el-dialog v-model="dialogVisible" title="编辑用户" width="500px" :close-on-click-modal="false">
            <el-form ref="userForm" :model="userForm" :rules="formRules" label-width="80px">
                <el-form-item label="昵称" prop="nickname">
                    <el-input v-model="userForm.nickname" placeholder="请输入昵称" />
                </el-form-item>

                <el-form-item label="头像">
                    <div class="avatar-upload">
                        <!-- 显示当前头像 -->
                        <div class="avatar-preview" v-if="userForm.avatar">
                            <el-image :src="userForm.avatar" class="avatar-image" :preview-src-list="[userForm.avatar]"
                                fit="cover" />
                            <div class="avatar-actions">
                                <el-button type="danger" size="small" @click="removeAvatar">
                                    <el-icon>
                                        <Delete />
                                    </el-icon>
                                    删除
                                </el-button>
                            </div>
                        </div>

                        <!-- 上传组件 -->
                        <el-upload class="avatar-uploader" :auto-upload="false" :show-file-list="false"
                            :on-change="handleAvatarChange" accept="image/*">
                            <el-button type="primary">
                                <el-icon>
                                    <Upload />
                                </el-icon>
                                {{ userForm.avatar ? '更换头像' : '上传头像' }}
                            </el-button>
                            <template #tip>
                                <div class="el-upload__tip">
                                    支持 jpg、png、gif 格式，大小不超过 20MB
                                </div>
                            </template>
                        </el-upload>
                    </div>
                </el-form-item>

                <el-form-item label="性别">
                    <el-radio-group v-model="userForm.gender">
                        <el-radio :label="0">未知</el-radio>
                        <el-radio :label="1">男</el-radio>
                        <el-radio :label="2">女</el-radio>
                    </el-radio-group>
                </el-form-item>

                <el-form-item label="状态">
                    <el-switch v-model="userForm.status" :active-value="1" :inactive-value="0" />
                    <span class="switch-label">{{ userForm.status ? '正常' : '禁用' }}</span>
                </el-form-item>
            </el-form>

            <template #footer>
                <el-button @click="dialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitUserForm">确认</el-button>
            </template>
        </el-dialog>

        <!-- 重置密码对话框 -->
        <el-dialog v-model="resetPasswordDialogVisible" title="重置密码" width="400px">
            <el-form ref="resetPasswordForm" :model="resetPasswordForm" :rules="resetPasswordRules" label-width="80px">
                <el-form-item label="新密码" prop="password">
                    <el-input v-model="resetPasswordForm.password" placeholder="请输入新密码" type="password" show-password />
                </el-form-item>
                <el-form-item label="确认密码" prop="confirmPassword">
                    <el-input v-model="resetPasswordForm.confirmPassword" placeholder="请确认新密码" type="password"
                        show-password />
                </el-form-item>
            </el-form>

            <template #footer>
                <el-button @click="resetPasswordDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitResetPassword">确认重置</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import { ElMessage } from 'element-plus';
import { getUploadSignature, uploadToOSS } from '@/utils/uploadUtils';

import {
    Upload, User, Refresh, Search, Edit, Delete, Key
} from '@element-plus/icons-vue';

export default {
    name: 'UserManagement',
    components: { Upload, User, Refresh, Search, Edit, Delete, Key },
    data() {
        // 密码确认验证
        const validateConfirmPassword = (rule, value, callback) => {
            if (value !== this.resetPasswordForm.password) {
                callback(new Error('两次输入的密码不一致'));
            } else {
                callback();
            }
        };

        return {
            loading: {
                list: false,
                refresh: false
            },
            searchForm: {
                phone: '',
                nickname: '',
                status: ''
            },
            userList: [],
            pagination: {
                page: 1,
                pageSize: 10,
                total: 0
            },
            dialogVisible: false,
            resetPasswordDialogVisible: false,
            userForm: {
                id: null,
                nickname: '',
                avatar: '',
                gender: 0,
                status: 1
            },
            resetPasswordForm: {
                id: null,
                password: '',
                confirmPassword: ''
            },
            formRules: {
                nickname: [
                    { required: true, message: '请输入昵称', trigger: 'blur' }
                ]
            },
            resetPasswordRules: {
                password: [
                    { required: true, message: '请输入新密码', trigger: 'blur' },
                    { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
                ],
                confirmPassword: [
                    { required: true, message: '请确认新密码', trigger: 'blur' },
                    { validator: validateConfirmPassword, trigger: 'blur' }
                ]
            }
        };
    },
    computed: {
        userInfo() {
            return JSON.parse(Cookies.get('pc_user_info') || '{}');
        }
    },
    mounted() {
        this.fetchUserList();
    },
    methods: {
        // 获取用户列表
        async fetchUserList() {
            this.loading.list = true;
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/pc/api/user_management.php`,
                    {
                        action: 'list',
                        store_id: this.userInfo.store_id,
                        user_id: this.userInfo.id,
                        ...this.searchForm,
                        page: this.pagination.page,
                        page_size: this.pagination.pageSize
                    }
                );

                if (response.data.data) {
                    this.userList = response.data.data;
                    this.pagination.total = response.data.pagination.total;
                }
            } catch (error) {
                ElMessage.error('获取用户列表失败');
                console.error('获取用户列表错误:', error);
            } finally {
                this.loading.list = false;
            }
        },

        // 处理头像选择变化
        async handleAvatarChange(file) {
            try {
                this.$message.info('头像上传中，请稍候...');
                const url = await this.uploadImage(file.raw);
                this.userForm.avatar = url;
                this.$message.success('头像上传成功');
            } catch (error) {
                this.$message.error(`头像上传失败: ${error.message}`);
                console.error('头像上传错误:', error);
            }
        },

        // 上传图片到OSS
        async uploadImage(file) {
            try {
                const loading = this.$loading({
                    lock: true,
                    text: '图片上传中...',
                    spinner: 'el-icon-loading',
                    background: 'rgba(0, 0, 0, 0.7)'
                });

                // 文件验证
                const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp'];
                const maxSize = 20 * 1024 * 1024;

                if (allowedTypes.indexOf(file.type) === -1) {
                    throw new Error(`不支持的文件类型: ${file.type}，仅支持 ${allowedTypes.join(', ')}`);
                }

                if (file.size > maxSize) {
                    throw new Error(`文件大小超过限制 (20MB): ${(file.size / 1024 / 1024).toFixed(20)}MB`);
                }

                const signature = await getUploadSignature(file.type.split('/')[0]);
                const res = await uploadToOSS(file, signature);

                loading.close();
                return res.url;
            } catch (error) {
                this.$loading().close();
                console.error('图片上传失败:', error);
                throw error;
            }
        },

        // 删除头像
        removeAvatar() {
            this.userForm.avatar = '';
        },

        // 处理搜索
        handleSearch() {
            this.pagination.page = 1;
            this.fetchUserList();
        },

        // 打开编辑对话框
        openEditDialog(row) {
            this.userForm = {
                id: row.id,
                nickname: row.nickname,
                avatar: row.avatar || '',
                gender: row.gender,
                status: row.status
            };
            this.dialogVisible = true;
        },

        // 提交用户表单
        submitUserForm() {
            this.$refs.userForm.validate(async (valid) => {
                if (!valid) {
                    ElMessage.warning('请完善表单信息');
                    return;
                }

                try {
                    const payload = {
                        ...this.userForm,
                        store_id: this.userInfo.store_id,
                        user_id: this.userInfo.id
                    };

                    const response = await axios.post(
                        `${config.BASE_URL}/pc/api/user_management.php`,
                        {
                            action: 'update',
                            ...payload
                        }
                    );

                    if (response.data.success) {
                        ElMessage.success('用户更新成功');
                        this.dialogVisible = false;
                        this.fetchUserList();
                    } else {
                        ElMessage.error(response.data.error || '操作失败');
                    }
                } catch (error) {
                    ElMessage.error('操作失败');
                    console.error('操作错误:', error);
                }
            });
        },

        // 打开重置密码对话框
        openResetPasswordDialog(row) {
            this.resetPasswordForm = {
                id: row.id,
                password: '',
                confirmPassword: ''
            };
            this.resetPasswordDialogVisible = true;
        },

        // 提交重置密码
        submitResetPassword() {
            this.$refs.resetPasswordForm.validate(async (valid) => {
                if (!valid) {
                    ElMessage.warning('请完善密码信息');
                    return;
                }

                try {
                    const response = await axios.post(
                        `${config.BASE_URL}/pc/api/user_management.php`,
                        {
                            action: 'reset_password',
                            store_id: this.userInfo.store_id,
                            user_id: this.userInfo.id,
                            id: this.resetPasswordForm.id,
                            password: this.resetPasswordForm.password
                        }
                    );

                    if (response.data.success) {
                        ElMessage.success('密码重置成功');
                        this.resetPasswordDialogVisible = false;
                        this.fetchUserList();
                    } else {
                        ElMessage.error(response.data.error || '密码重置失败');
                    }
                } catch (error) {
                    ElMessage.error('密码重置失败');
                    console.error('重置密码错误:', error);
                }
            });
        }
    }
};
</script>

<style scoped lang="scss">
.page-container {
    padding: 20px;
    background: #f5f7fa;
    min-height: 100vh;
}

.settings-form {
    max-width: 95%;
    margin: 0 auto;
}

/* 引导区域 */
.guide-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
    padding: 24px;
    background: linear-gradient(135deg, #409eff 0%, #3375b9 100%);
    border-radius: 12px;
    color: white;
    box-shadow: 0 4px 12px rgba(64, 158, 255, 0.2);
}

.guide-content {
    .guide-title {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 24px;
        font-weight: 600;
        margin-bottom: 8px;

        .guide-icon {
            font-size: 28px;
        }
    }

    .guide-desc {
        font-size: 14px;
        opacity: 0.9;
        margin: 0;
    }
}

/* 搜索区域 */
.search-bar {
    display: flex;
    align-items: center;
    margin-bottom: 24px;
    padding: 16px;
    background: #f8fafc;
    border-radius: 8px;
}

/* 分页容器 */
.pagination-container {
    margin-top: 24px;
    display: flex;
    justify-content: flex-end;
}

/* 开关标签 */
.switch-label {
    margin-left: 10px;
    color: #606266;
    font-size: 14px;
}

/* 响应式设计 */
@media (max-width: 768px) {
    .guide-section {
        flex-direction: column;
        gap: 16px;
        text-align: center;
    }

    .search-bar {
        flex-direction: column;
        gap: 12px;

        .el-input,
        .el-select {
            width: 100% !important;
            margin-right: 0 !important;
        }

        .el-button {
            width: 100%;
            float: none !important;
        }
    }
}

// 头像上传样式
.avatar-upload {
    display: flex;
    align-items: flex-start;
    gap: 20px;
}

.avatar-preview {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
}

.avatar-image {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    border: 2px solid #ebeef5;
    overflow: hidden;
}

.avatar-actions {
    display: flex;
    justify-content: center;
}

.avatar-uploader {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.el-upload__tip {
    font-size: 12px;
    color: #909399;
    margin-top: 5px;
}

// 响应式调整
@media (max-width: 768px) {
    .avatar-upload {
        flex-direction: column;
        align-items: center;
    }
}
</style>